import { Component, HostBinding, NgZone } from '@angular/core'
import { CommonModule, Location } from '@angular/common'
import { Title } from '@angular/platform-browser'
import { Router, RouterModule } from '@angular/router'
import { NgbDropdownModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap'
import { TranslateModule } from '@ngx-translate/core'
import { LocalStorage } from 'ngx-store'

import { APIService } from 'api'
import { SpinnerIconComponent } from 'common/util/components/spinnerIcon.component'
import { NotifyService } from 'common/util/services/notify.service'
import { OperatingSystemService } from 'common/util/services/operatingSystem.service'
import { RouteLoggerService } from 'common/util/services/routeLogger.service'
import { BadgeModule } from 'common/util.badge'
import { UtilReleaseNotesModule } from 'common/util.releaseNotes'
import { IdentityService } from 'main/services/identity.service'
import { ParametersService } from 'main/services/parameters.service'
import { NotificationModule } from 'notification'
import { NotificationHistoryService } from 'notification/services/notificationHistory.service'
import { PanelService } from 'panel/services/panel.service'
import { ProxyCacheService } from 'panel/services/proxyCache.service'
import { routeChangingTrigger } from 'root/components/appRoot.component'
import { UploaderPanelComponent } from 'uploader'

@Component({
  selector: 'panel-root',
  templateUrl: './panelRoot.component.html',
  styleUrls: ['./panelRoot.component.scss'],
  animations: [routeChangingTrigger],
  imports: [
    CommonModule,
    TranslateModule,
    NgbTooltipModule,
    RouterModule,
    UtilReleaseNotesModule,
    NgbDropdownModule,
    BadgeModule,
    NotificationModule,
    SpinnerIconComponent,
    UploaderPanelComponent,
  ],
})
export class PanelRootComponent {
  proxyMode = false
  pathProxyMap = {}
  @LocalStorage('proxyPanelVisible') proxyPanelVisible = false
  navigated = false

  get hostConnected (): boolean {
    return !this.panel.isUxpPanel || this.panel.active
  }

  constructor (
    private api: APIService,
    private notify: NotifyService,
    public identity: IdentityService,
    public panel: PanelService,
    public proxyCache: ProxyCacheService,
    public notifyHistory: NotificationHistoryService,
    private zone: NgZone,
    public parameters: ParametersService,
    public router: Router,
    public routeLogger: RouteLoggerService,
    private location: Location,
    public title: Title,
    private operatingSystem: OperatingSystemService,
  ) { }

  async ngOnInit () {
    await this.panel.ready
    if (this.panel.plugin.canToggleProxyMode && ['PPRO', 'Resolve', 'MediaComposer'].includes(this.panel.app)) {
      setInterval(async () => {
        const proxyMode = await this.panel.plugin.isProxyMode()
        if (proxyMode !== this.proxyMode) {
          this.zone.run(() => {
            this.proxyMode = proxyMode
          })
        }
      }, 1000)
    } else if (this.panel.app === 'AEFT') {
      this.proxyPanelVisible = false
    }

    if (this.panel.native) {
      const proxyPath = this.panel.plugin.getConfig('proxyPath')
      try {
        if (this.checkProxyPath(proxyPath)) {
          this.panel.plugin.setConfig('proxyPath', proxyPath)
          this.proxyCache.cachePath = proxyPath
        }
      } catch {
        return
      }
    }
  }

  restart () {
    this.panel.restartExtension()
  }

  goHome () {
    this.router.navigate(['/media'])
  }

  canGoBack () {
    return this.routeLogger.hadInAppNavigation
  }

  goBack () {
    this.location.back()
  }

  async cacheAndAttachSequenceProxies () {
    const paths = await this.panel.plugin.getActiveSequencePaths()
    await this.cacheProxies(paths)
    this.attachProxies(paths)
  }

  async cacheAndAttachProjectProxies () {
    const paths = await this.panel.plugin.getProjectPaths()
    await this.cacheProxies(paths)
    this.attachProxies(paths)
  }

  private getPreferredProxyId (asset) {
    const preferredProxy
      = (asset.proxies ?? []).find((p) => p.sourceAudioLayoutPreserved) ?? asset.defaultProxy
    return preferredProxy?.id
  }

  async cacheProxies (paths) {
    const root = this.panel.plugin.currentMediaRootID
    let foundAssets = await this.api.private.locateProxies({
      locateProxiesEndpointRequest: {
        paths,
      },
      forRoot: root,
      includeProxies: true,
    })
    foundAssets = foundAssets.filter((x) => x.asset.type === 'video')
    await Promise.all(
      foundAssets.map((x) =>
        this.proxyCache.scheduleCacheAsset(x.asset, this.getPreferredProxyId(x.asset)),
      ),
    )
  }

  async updateProxyPathMap (paths) {
    const root = this.panel.plugin.currentMediaRootID
    const foundAssets = await this.api.private.locateProxies({
      locateProxiesEndpointRequest: {
        paths,
      },
      forRoot: root,
      includeProxies: true,
    })
    for (const foundAsset of foundAssets) {
      const preferredProxyId = this.getPreferredProxyId(foundAsset.asset)
      if (await this.proxyCache.isCached(foundAsset.asset, preferredProxyId)) {
        this.pathProxyMap[foundAsset.path] = this.proxyCache.getProxyPath(
          foundAsset.syncId,
          preferredProxyId,
        )
      }
    }
  }

  async attachProxies (paths) {
    await this.updateProxyPathMap(paths)
    // Hosts that expose a proxy display toggle (CEP/Resolve/MediaComposer)
    // switch to proxy mode on attach. Premiere UXP has no such toggle, so we
    // just attach — Premiere's own Enable Proxies switch controls display.
    if (this.panel.plugin.canToggleProxyMode) {
      this.setProxyMode(true)
    }
    this.panel.plugin.attachProxies(this.pathProxyMap)
  }

  async proxyDirChange () {
    if (this.proxyCache.runningJobs.length) {
      return
    }
    const result = await this.panel.plugin.getLocalPath({
      defaultPath: this.proxyCache.cachePath,
      properties: ['openDirectory'],
    })

    if (result && !this.proxyCache.runningJobs.length) {
      try {
        this.checkProxyPath(result)
      } catch (error) {
        this.notify.error(error)
        return
      }

      this.panel.plugin.setConfig('proxyPath', result)
      this.proxyCache.cachePath = result
    }
  }

  checkProxyPath (proxyPath) {
    if (!this.panel.plugin.hasProxy) {
      return false
    }
    const path = window['nodeRequire']('path')
    if (!proxyPath) {
      return false
    }

    let stats
    const isCep = this.panel.app === 'PPRO' && !this.panel.isUxpPanel

    if (isCep) {
      stats = this.panel.fs.stat(proxyPath).data
    } else {
      stats = this.panel.fs.statSync(proxyPath)
    }

    if (!stats.isDirectory()) {
      throw new Error('Proxy path is not a directory')
    }

    const testpath = path.join(proxyPath, '.testwrite.txt')
    try {
      if (isCep) {
        this.panel.fs.writeFile(testpath, 'test')
        this.panel.fs.deleteFile(testpath)
      } else {
        this.panel.fs.writeFileSync(testpath, 'test')
        this.panel.fs.unlinkSync(testpath)
      }
    } catch {
      throw new Error('Unable to write file in proxy path')
    }

    return true
  }

  setProxyMode (enabled) {
    this.proxyMode = enabled
    this.panel.plugin.setProxyMode(enabled)
  }

  syncMetadata () {
    this.panel.syncMetadata()
  }

  @HostBinding('class.uxp-host-mac') get isUxpHostMac () {
    return this.panel.isUxpPanel && this.operatingSystem.get() === 'macOS'
  }
}
